import { DecimalPipe } from '@angular/common';
import { PickerColumn } from '@ionic/core';
import crypto from 'crypto';
import * as moment from 'moment-timezone';

import {
  OBJECTIVE_IMG_MAP,
  CHILD_ICON_IMG_MAP,
  CASH_NEMO,
  CASH_PERCENTAGE,
  UINT32_MAX,
  CURRENCY_PREFIX,
  CURRENCY_DECIMALS,
  CURRENCY_LOCALE,
  Currencies,
  OBJECTIVE_NAME_MAP,
} from '@constants/miscellaneous';
import { IAssetValuesPosition } from '@interfaces/assetValue';
import { IContactsMap } from '@interfaces/contacts';
import { IDefault, IDefaultAnswers } from '@interfaces/default-interfaces';
import { IGoals } from '@interfaces/goals';
import { IContributionStrategyItem } from '@interfaces/movements';
import { IChildInfoQuestionnaire } from '@interfaces/personal-info';
import { IAssetName } from '@interfaces/portfolio-constants';
import { IPortfolio } from '@interfaces/portfolios';
import { ICounties } from '@interfaces/user-information-constants';
import { FirebaseService } from '@services/firebase/firebase.service';

export default class Utils {
  static decimalPipe = new DecimalPipe('es');

  static increment;

  static capitalizeFirstLetter(text: string) {
    text = text.toLowerCase();
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  static getYearsLabel(years) {
    return years === 1 ? ' año' : ' años';
  }

  static getHorizonLevel(newLevel: number, max: number, min: number) {
    const horizonObject = {
      shortName: '',
      name: '',
      class: '',
    };
    if (newLevel >= max) {
      horizonObject.shortName = 'largo';
      horizonObject.name = 'Largo Plazo';
      horizonObject.class = 'long-term';
    } else if (newLevel <= min) {
      horizonObject.shortName = 'corto';
      horizonObject.name = 'Corto Plazo';
      horizonObject.class = 'short-term';
    } else {
      horizonObject.shortName = 'mediano';
      horizonObject.name = 'Mediano Plazo';
      horizonObject.class = 'medium-term';
    }
    return horizonObject;
  }

  static getRiskLevelLabel(newLevel: number, max: number, min: number) {
    const riskObject = {
      shortName: '',
      name: '',
      class: '',
    };
    if (newLevel >= max) {
      riskObject.shortName = 'alto';
      riskObject.name = 'Riesgo alto';
      riskObject.class = 'short-term';
    } else if (newLevel <= min) {
      riskObject.shortName = 'bajo';
      riskObject.name = 'Riesgo bajo';
      riskObject.class = 'long-term';
    } else {
      riskObject.shortName = 'moderado';
      riskObject.name = 'Riesgo moderado';
      riskObject.class = 'medium-term';
    }
    return riskObject;
  }

  static yearsPickerOptions(max: number, buttonText: string, columnName: string, currentIndex: number) {
    const optionsArray = [];
    let object = {
      text: '1 año',
      value: 1,
    };
    optionsArray.push(object);
    for (let index = 2; index <= max; index++) {
      object = {
        text: `${index} años`,
        value: index,
      };
      optionsArray.push(object);
    }
    return {
      buttons: [{
        text: buttonText,
      }],
      columns: [
      {
        name: columnName,
        options: optionsArray,
        selectedIndex: currentIndex,
      } as PickerColumn,
      ],
    };
  }

  static differenceInMinutes(firstDate: Date, secondDate: Date) {
    const timeDiff = Math.abs(firstDate.getTime() - secondDate.getTime());
    const diffMins = Math.round(((timeDiff % 86400000) % 3600000) / 60000);
    return diffMins;
  }

  static getTodaysDate() {
    const today = new Date();
    const yyyy = today.getFullYear();
    let dd = today.getDate().toString();
    let mm = (today.getMonth() + 1).toString();

    if (parseInt(dd, 10) < 10) {
      dd = '0' + dd;
    }

    if (parseInt(mm, 10) < 10) {
      mm = '0' + mm;
    }

    return dd + '/' + mm + '/' + yyyy;
  }

  static getFormattedDate(date: Date) {
    const yyyy = date.getFullYear();
    let dd = date.getDate().toString();
    let mm = (date.getMonth() + 1).toString();
    if (parseInt(dd, 10) < 10) {
      dd = '0' + dd;
    }
    if (parseInt(mm, 10) < 10) {
      mm = '0' + mm;
    }
    return dd + '/' + mm + '/' + yyyy;
  }

  static formatToMoney(value: string) {
    if (!value || value === 'NaN') {
      return '';
    }
    if (value.includes('-')) {
      return '-$' + value.replace(/-/g, '').replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
    }
    return '$' + value.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
  }

  static formatToRut(value: string) {
    if (!value) {
      return null;
    }
    const currentValue = value.replace(/^0+/, '');
    if (currentValue !== '' && currentValue.length > 1) {
      const noDots = currentValue.replace(/\./g, '');
      const cleanValueNumber = noDots.replace(/-/g, '');
      const cleanValue = cleanValueNumber.toString();
      const beginning = cleanValue.substring(0, cleanValue.length - 1);
      let letra = '';
      let rut = '';
      let j = 1;
      for (let i = beginning.length - 1; i >= 0; i--) {
        letra = beginning.charAt(i);
        rut = letra + rut;
        if (j % 3 === 0 && j <= beginning.length - 1) {
          rut = '.' + rut;
        }
        j++;
      }
      const checkDigit = cleanValue.substring(cleanValue.length - 1);
      rut = rut + '-' + checkDigit;
      return rut;
    } else {
      if (isNaN(parseInt(currentValue, 10))) {
        return '';
      }
      return currentValue;
    }
  }

  static formatToCellPhone(value: string) {
    if (value !== '' && value.length > 1) {
      let cleanValue = value.replace('+56', '').replace(/ /g, '');
      if (cleanValue.length > 1) {
      cleanValue = cleanValue.slice(0, 1) + ' ' + cleanValue.slice(1);
      }
      if (cleanValue.length > 10) {
        cleanValue = cleanValue.substring(0, 10);
      } else if (cleanValue.length > 6) {
        cleanValue = cleanValue.substring(0, 6) + ' ' + cleanValue.substring(6, cleanValue.length);
      }
      return '+56 ' + cleanValue;
    }
    return '+56';
  }

  static convertDefaultToAnswer(defaultArray: IDefault[], extraField: string = null) {
    const answers: IDefaultAnswers[] = [];
    for (let index = 0; index < defaultArray['names'].length; index++) {
      const element: IDefaultAnswers = {
        statement: '',
        value: '',
      };
      element.statement = defaultArray['names'][index].name;
      element.value = defaultArray['names'][index].id;
      if (extraField) { element['extra'] = defaultArray['names'][index][extraField]; }
      answers.push(element);
    }
    return answers;
  }

  static getCurrentCounties(region: string, counties: ICounties[]) {
    const answers: IDefaultAnswers[] = [];
    for (let index = 0; index < counties.length; index++) {
      if (counties[index].region === region) {
        const element: IDefaultAnswers = {
          statement: '',
          value: '',
        };
        element.statement = counties[index].name;
        element.value = counties[index].id;
        answers.push(element);
      }
    }
    return answers;
  }

  static getValueInJSONArray(array: any, key: string, valueToFind: string, keyToReturn: string) {
    for (let index = 0; index < array.length; index++) {
      if (array[index][key] === valueToFind) {
        return array[index][keyToReturn];
      }
    }
  }

  static getIndexInJSONArray(array: any, key: string, valueToFind: string) {
    for (let index = 0; index < array.length; index++) {
      if (array[index][key] === valueToFind) {
        return index;
      }
    }
    return null;
  }

  static getAvailablePortfolios(portfolios: IPortfolio[], userGoals: IGoals[]) {
    const availablePortfolios: IPortfolio[] = [];
    let hasGoal = false;
    for (let index = 0; index < portfolios.length; index++) {
      for (let indexGoals = 0; indexGoals < userGoals.length; indexGoals++) {
        if (portfolios[index].id === userGoals[indexGoals].portfolioId) {
          hasGoal = true;
          break;
        }
      }
      if (!hasGoal) {
        availablePortfolios.push(portfolios[index]);
      }
      hasGoal = false;
    }
    return availablePortfolios;
  }

  static sortJSONByKey(prop) {
    return function (firstElement, secondElement) {
      if ( firstElement[prop] > secondElement[prop]) {
        return 1;
      } else if ( firstElement[prop] < secondElement[prop] ) {
        return -1;
      }
      return 0;
    };
  }

  static sortJSONArrayByArrayKey(originalArray: any[], originalProp: string, copyArray: any[], copyProp: string) {
    const newArray = [];
    let arrayIndex = 0;
    for (let index = 0; index < copyArray.length; index++) {
      const element = copyArray[index];
      arrayIndex = this.getIndexInJSONArray(originalArray, originalProp, element[copyProp]);
      if (arrayIndex !== null) {
        newArray.push(originalArray[arrayIndex]);
      }
    }
    return newArray;
  }

  static sortJSONArrayByKey(array: any, key: string) {
    return array.sort(function (a, b) {
      const x = a[key];
      const y = b[key];
      return ((x < y) ? -1 : ((x > y) ? 1 : 0));
    });
  }

  static roundToNearestHundred(n: number) {
    return n < 100 ? 100 : Math.ceil(n / 100) * 100;
  }

  static roundToNearestThousand(n: number) {
    return n < 1000 ? this.roundToNearestHundred(n) : Math.ceil(n / 1000) * 1000;
  }

  static getStepsArrayByValue(value: string) {
    const currentValue = parseInt(value.replace(/\D/g, ''), 10);
    const amountOfSteps = 10;
    const steps = [];
    const incrementalDifference = 5;
    let currentStep = 0;
    steps.push(0);
    for (let index = 1; index < amountOfSteps; index++) {
      currentStep = this.roundToNearestThousand(
        Math.pow(
          index * incrementalDifference, (Math.log10(currentValue) / Math.log10((amountOfSteps - 1) * incrementalDifference)),
        ),
      );
      steps.push(currentStep);
    }
    return steps;
  }

  static getSortedArrayWithIndexes(array: any[]) {
    const arrayWithIndex = [];
    const indexes = [];
    const sortedArray = [];
    const returnArray = {};
    for (let index = 0; index < array.length; index++) {
      arrayWithIndex.push([array[index], index]);
    }
    arrayWithIndex.sort(function (left, right) {
      return left[0] < right[0] ? -1 : 1;
    });

    for (let index = 0; index < arrayWithIndex.length; index++) {
      sortedArray.push(arrayWithIndex[index][0]);
      indexes.push(arrayWithIndex[index][1]);
    }
    returnArray['array'] = sortedArray;
    returnArray['indexes'] = indexes;
    return returnArray;
  }

  static getNearestIndexInArrayByValue(value: number, array: number[]) {
    const sortedArray = [...array].sort((a, b) => a - b);
    let nearestIndex = 0;
    if (value > 0) {
      for (let index = 0; index < sortedArray.length; index++) {
        if (sortedArray[index] > value) {
          nearestIndex = index;
          break;
        }
        nearestIndex = index;
      }
    }
    return nearestIndex;
  }

  static getGoalGradientClass(goalPercentage: number) {
    const highPercentage = 100;
    const midPercentage = 60;
    let gradientClass = 'danger';
    if (goalPercentage >= highPercentage) {
      gradientClass = 'success';
    } else if (goalPercentage > midPercentage) {
      gradientClass = 'warning';
    }
    return gradientClass;
  }

  static getUniqueNameByArray(value: string, array: any[]) {
    let index = 2;
    let newValue = value;
    while (array.indexOf(newValue) > -1) {
      newValue = `${value} ${index}`;
      index++;
    }
    return newValue;
  }

  static isObjectWithNullValues(object: object, compareKeys: string[]) {
    for (const key in object) {
      if (object.hasOwnProperty(key)) {
        if (object[key] && compareKeys.includes(key)) {
        return false;
        }
      }
     }
    return true;
  }

  static createYAxisLabels(goal: number, projection: number, initial: number) {
    const axisLabels = [];
    let high;
    if (goal > projection) {
    high = goal;
    } else {
    high = projection;
    }
    high = high + (high - initial) / 20; // Add 5% more up

    Utils.increment = 10000; // 10k
    if (high - initial > 10000000) { // > 10M
    Utils.increment = 1000000; // 1M
    } else if (high - initial > 1000000) { // > 1M
    Utils.increment = 100000; // 0.1M
    }
    const value = Math.ceil(initial / Utils.increment) * Utils.increment;
    axisLabels.push(value);
    const secondValue = Math.round((value + ((high - initial) / 4.2) * 1) / Utils.increment) * Utils.increment;
    axisLabels.push(secondValue);

    const ticksDiff = secondValue - value;
    let nValue = secondValue + ticksDiff;
    while (nValue < high && ticksDiff) {
    axisLabels.push(nValue);
    nValue = nValue + ticksDiff;
    }

    return axisLabels;
  }

  static shortLabel(value: number) {
    if (!value) { return '$0'; }

    const is10kAprox = Utils.increment === 10000;
    const increment = is10kAprox ? 1000 : 1000000;
    const magnitude = is10kAprox ? 'K' : 'M';

    let label = (value / increment).toFixed(is10kAprox ? 0 : 1);
    if (label.endsWith('.0')) {
    label = label.slice(0, -2);
    }

    return `$${label}${magnitude}`.replace('.', ',');
  }

  static addPointsToCurves(series: number[][]) {
    if (!series[0] || series[0].length > 40) {
    return series;
    }

    const newSeries = series.map((serie) => {
    const newSerie = [];
    for (let i = 0; i < serie.length - 1; i++) {
      newSerie.push(serie[i]);
      newSerie.push((serie[i] + serie[i + 1]) / 2);
    }
    newSerie.push(serie[serie.length - 1]);
    return newSerie;
    });

    return Utils.addPointsToCurves(newSeries);
  }

  static getObjectiveIcon(objectiveId: number, childInfo?: IChildInfoQuestionnaire) {
    if (childInfo) {
    return CHILD_ICON_IMG_MAP[childInfo.icon];
    }
    return OBJECTIVE_IMG_MAP[objectiveId] || OBJECTIVE_IMG_MAP[4];
  }

  static getObjectiveName(objectiveId: number) {
    return OBJECTIVE_NAME_MAP[objectiveId] || OBJECTIVE_NAME_MAP[4];
  }

  static addDays(date: Date, days: number) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  static getDateFormat(date: Date) {
    const now = new Date();
    if (date.getFullYear() === now.getFullYear()) {
      return 'd \'de\' MMMM';
    } else {
      return 'd \'de\' MMMM yyyy';
    }
  }

  static getLocalCalendarTextFromChileDateString(dateString: string, useBeforeLabel = false) {
    moment.locale('es');
    const formattedString = dateString.split(' ')[0].replace(',', '').split('-').reverse().join('-') + ' ' + dateString.split(' ')[1];
    const chileMoment = moment.tz(formattedString, 'America/Santiago');

    const calendarConfig = {
      sameDay: `[hoy día ${useBeforeLabel ? 'antes de las' : 'a las'}] H:mm`,
      nextDay: `[mañana ${useBeforeLabel ? 'antes de las' : 'a las'}] H:mm`,
      nextWeek: `[el] dddd [${useBeforeLabel ? 'antes de las' : 'a las'}] H:mm`,
      lastDay: '[ayer a las] H:mm',
      lastWeek: '[el] dddd [a las] H:mm',
      sameElse: '[el] dddd D [de] MMMM',
    };

    return moment.tz(chileMoment, moment.tz.guess()).calendar(calendarConfig);
  }

  static debounce(fn, wait) {
    let timeout;
    return (...args) => {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(context, args), wait);
    };
  }

  static calculateCashExcess(lastHistoricEvolution) {
    if (!(lastHistoricEvolution && lastHistoricEvolution.totalAmount)) {
      return 0;
    }
    const assets = lastHistoricEvolution.effectivePortfolioComposition;
    const cashAsset = assets.find((a) => a.assetId === CASH_NEMO);
    const cashExcess = cashAsset.amount - (lastHistoricEvolution.totalAmount * CASH_PERCENTAGE);
    return Math.max(cashExcess, 0);
  }

  static calculateAssetsToBuy(
    depositAmount: number,
    strategy: IContributionStrategyItem[],
    portfolios: IPortfolio[],
    assetNames: IAssetName[],
    assetValues: IAssetValuesPosition[],
    useCashExcess = true,
  ) {
    const assetDistribution = [];
    strategy.forEach(portfolioInContribution => {
      let portfolioDepositAmount = depositAmount * portfolioInContribution.percentage;
      if (portfolioDepositAmount > 0) {
        const portfolio = portfolios.find((p) => p.id === portfolioInContribution.portfolioId);
        const portfolioStrategy = portfolio.strategicPortfolioComposition.portfolio;
        if (!portfolio.cashExcessInProgress && !portfolio.withdrawalProgressAmount) {
          portfolioDepositAmount += useCashExcess ? Utils.calculateCashExcess(portfolio.lastHistoricEvolution) : 0;
        }
        portfolioStrategy.forEach(asset => {
          if (asset.percentage > 0) {
            const assetInDistribution = assetDistribution.find(p => p.assetId === asset.assetId);
            if (assetInDistribution) {
              assetInDistribution.amount += asset.percentage * portfolioDepositAmount;
              assetInDistribution.shares = Math.floor(assetInDistribution.amount / assetInDistribution.sharePrice);
            } else {
              const newAsset = {
                name: assetNames.find(p => p.assetId === asset.assetId).assetClass,
                sharePrice: asset.assetId !== CASH_NEMO ? assetValues.find(a => a.assetId === asset.assetId).sharePrice : 1,
                amount: asset.percentage * portfolioDepositAmount,
                assetId: asset.assetId,
                shares: 0,
              };
              newAsset.shares = Math.floor(newAsset.amount / newAsset.sharePrice);
              assetDistribution.push(newAsset);
            }
          }
        });
      }
    });
    assetDistribution.sort((a, b) => b.assetId === CASH_NEMO ? -1 : a.assetId === CASH_NEMO ? 1 : b.amount - a.amount);
    return assetDistribution;
  }

  static cleanContacts(contacts: any[]): IContactsMap {
    const contactsMap = {};
    contacts.forEach(c => {
      if (c.phoneNumbers && c.phoneNumbers.length) {
        const multipleNumbers = c.phoneNumbers.length > 1 &&
                                c.phoneNumbers[0].value.replace(/[^0-9]+/g, '') !==
                                c.phoneNumbers[1].value.replace(/[^0-9]+/g, '');
        c.phoneNumbers.forEach(n => {
          const cleanedPhone = n.value.replace(/[^0-9]+/g, '');
          const contactName = `${c.firstName}${c.middleName ? ' ' + c.middleName : ''}${c.familyName ? ' ' + c.familyName : ''}`;
          let cleanedName = (contactName || 'N N').replace(/\s\s+/g, ' ').trim();
          if (cleanedName.length > 40) {
            const cutAt = cleanedName.lastIndexOf(' ', 40);
            cleanedName = cleanedName.slice(0, (cutAt > 20 ? cutAt : 40));
          }
          if (cleanedPhone.length > 9 && cleanedPhone.length < 17 && !cleanedPhone.startsWith('562')) {
            contactsMap[+cleanedPhone] = {
              phoneId: c.id,
              savedName: cleanedName,
              name: cleanedName + (multipleNumbers ? ` (${cleanedPhone})` : ''),
            };
          }
        });
      }
    });
    return contactsMap;
  }

  static calculateDaysRemaingSmartlink(smartlink: any) {
    const expirationDate = moment(smartlink.expirationDate.toDate());
    const today = moment().utcOffset(0).set({ hour: 12, minute: 0, second: 0, millisecond: 0 });
    return expirationDate.diff(today, 'days');
  }

  static smartlinkExpiresThisMonth(smartlink: any) {
    const expirationDate = moment(smartlink.expirationDate.toDate());
    const currentMonth = moment().utcOffset(0).set({ hour: 12, minute: 0, second: 0, millisecond: 0 });
    return expirationDate.isSame(currentMonth, 'month') && expirationDate.isSame(currentMonth, 'year');
  }

  static getTimeString(timeArray: Array<number>, withSeconds: boolean = false) {
    let timeString = timeArray[0].toString() + ':' + this.getMinutesString(timeArray[1]);
    if (withSeconds) {
      if (timeArray.length > 2) {
        timeString += ':' + this.getSecondsString(timeArray[2]);
      } else {
        timeString += ':00';
      }
    }
    return timeString;
  }

  static getMinutesString(minutes: number) {
    if (minutes === 0) {
      return '00';
    } else {
      return minutes.toString();
    }
  }

  static getSecondsString(seconds: number) {
    if (seconds === 0) {
      return '00';
    } else {
      return seconds.toString();
    }
  }

  static isTimeBetween(dateToCheck: any, startTimeStr: string, endTimeStr: string, intraDay: boolean): boolean {
    const startTime = moment(startTimeStr, 'HH:mm');
    const endTime = moment(endTimeStr, 'HH:mm');
    const date = dateToCheck.toDate();
    const currentHour = date.getHours();
    const currentMinute = date.getMinutes();
    const timeToCheck = moment().set({ hours: currentHour, minutes: currentMinute });
    const result =
      intraDay ?
      timeToCheck.isAfter(startTime) || timeToCheck.isBefore(endTime)
      : timeToCheck.isAfter(startTime) && timeToCheck.isBefore(endTime);
    return result;
  }

  static isDaysExceeded(date1: Date, date2: Date, days: number): boolean {
    const diffInDays = Math.abs(moment(date1).diff(moment(date2), 'days'));
    return diffInDays >= days;
  }

  static shouldUseNewWithdrawalDate(newWithdrawalDate: Date, comparisonDate: Date, isQuery = false) {
    const withdrawalDate = isQuery ? this.addDays(newWithdrawalDate, 2) : newWithdrawalDate;
    return comparisonDate >= withdrawalDate;
  }

  static computeProbabilityOutcome(percentage: number): boolean {
    if (!percentage) { return false; }
    const generatedRandomInt: number = this.getRandomInt(0, 1000);
    const threshold: number = percentage * 1000;
    return generatedRandomInt < threshold;
  }

  static getRandomInt(min: number, max: number): number {
    const randomValue = crypto.randomBytes(4).readUInt32BE(0);
    const scaledValue = Math.floor((randomValue / UINT32_MAX) * (max - min + 1) + min);
    return scaledValue;
  }
  static getArrayRandomElement(arr: any[]): any {
    if (!arr) { return null; }
    const randomIndex = Math.floor(Math.random() * arr.length);
    return arr[randomIndex];
  }

  static firestoreTimestampToDate(timestamp: { seconds: number; nanoseconds: number }): Date {
    const milliseconds = timestamp.seconds * 1000 + timestamp.nanoseconds / 1000000;
    const date = new Date(milliseconds);
    return date;
  }

  static convertPointDateToDate(point: { date: any }): Date {
    if (!point || !point.date) {
      return new Date();
    }
    if (point.date.seconds !== undefined) {
      return new Date(point.date.seconds * 1000);
    }
    if (point.date instanceof Date) {
      return point.date;
    }
    return new Date(point.date);
  }

  static setEstimatedDate(firebaseService: FirebaseService) {
    const now = new Date();
    const futureDate = Utils.addDays(now, 2);
    const workingDay = firebaseService.getNextWorkingDay(futureDate, true);
    const workingDayChile = workingDay.toLocaleDateString('es-CL', {timeZone: 'America/Santiago'}).replace(/\-/g, '/');
    return workingDayChile;
  }

  static generateWithdrawalId(userId: string, portfolioId: string, amount: number): string {
    const timestamp = new Date().toISOString();
    return `${userId}_${timestamp}_${portfolioId}_${amount}`;
  }

  static formatAmountWithCurrency(amount: number, currency = Currencies.CLP): string {
    return `${CURRENCY_PREFIX[currency]}${this.decimalPipe.transform(amount, CURRENCY_DECIMALS[currency], CURRENCY_LOCALE[currency])}`;
  }

  static getAbsoluteValue(value: number): number {
    return Math.abs(value || 0);
  }

  static formatSpanishNumber(num: number): string {
    const parts = num.toFixed(2).split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const decimalPart = parts[1];
    return `${integerPart},${decimalPart}`;
  }

  static formatProfitLoss(value: number, isPercentage: boolean): { display: string; isPositive: boolean; showArrow: boolean } {
    if (value === 0 || value === null || value === undefined) {
      return {
        display: isPercentage ? '0,00%' : 'US$0,00',
        isPositive: true,
        showArrow: false,
      };
    }

    const formattedValue = isPercentage
      ? `${value.toFixed(2).replace('.', ',')}%`
      : `US$${Utils.formatSpanishNumber(Math.abs(value))}`;

    const prefix = value > 0 ? '+' : (isPercentage ? '' : '-');

    return {
      display: `${prefix}${formattedValue}`,
      isPositive: value >= 0,
      showArrow: true,
    };
  }

  static formatUSDAmount(value: number): { display: string; isPositive: boolean; showArrow: boolean } {
    if (value === 0) {
      return { display: 'US$0,00', isPositive: true, showArrow: false };
    }

    const formattedValue = `US$${Utils.formatSpanishNumber(Math.abs(value))}`;
    return {
      display: formattedValue,
      isPositive: value >= 0,
      showArrow: true,
    };
  }
}
